import React, { useEffect, useState } from 'react';
import styles from './styles.module.css';
import { useNavigate } from 'react-router-dom';
import { CarouselImage, CarouselNavigatorNames } from '../../../utils/CarouselNavigatorManager';

type Props = {
    navigatorName: CarouselNavigatorNames;
    visibleCount?: number; // Add a prop for the number of visible images
};

const CarouselNavigation: React.FC<Props> = ({ navigatorName, visibleCount = 3 }) => {
    const [_, setRerenderState] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [highlightedItem, setHighlightedItem] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const [images, setImages] = useState<CarouselImage[]>([]);
    const navigate = useNavigate();

    useEffect(() => {
        window.applicationManager.carouselNavigatorManager.addNavigator(navigatorName, setImages, setRerenderState, setHighlightedItem, setIsVisible, navigate);

        return () => {
            window.applicationManager.carouselNavigatorManager.removeNavigator(navigatorName);
        }
    }, [navigatorName]);

    useEffect(() => {
        if (highlightedItem) {
            const highlightedIndex = images.findIndex((image) => image.link === highlightedItem);
            if (highlightedIndex !== -1) {
                setCurrentIndex((prev) => {
                    if (highlightedIndex < prev) {
                        return highlightedIndex; // Scroll left to make it visible
                    } else if (highlightedIndex >= prev + visibleCount) {
                        return highlightedIndex - (visibleCount - 1); // Scroll right to make it visible
                    }
                    return prev; // Already visible
                });
            }
        }
    }, [highlightedItem, images, visibleCount]);


    const handlePrev = () => {
        setHighlightedItem(null);
        setCurrentIndex((prev) => Math.max(prev - 1, 0));
    };

    const handleNext = () => {
        setHighlightedItem(null);
        setCurrentIndex((prev) => Math.min(prev + 1, images.length - visibleCount));
    };

    const handleImageClick = (link: string) => {
        setHighlightedItem(null);
        navigate(link);
    };

    return (
        isVisible ? <div className={styles.carouselContainer}>
            <button className={`${styles.arrow} ${styles.left}`} onClick={handlePrev}>
                &#8249;
            </button>
            <div className={styles.carousel}>
                <div
                    className={styles.carouselImages}
                    style={{ transform: `translateX(-${currentIndex * (100 / visibleCount)}%)` }}
                >
                    {images.map((image) => {
                        const isHighlighted = highlightedItem === image.link;
                        return (
                            <img
                                key={image.link}
                                src={image.src}
                                alt={`Image ${image.link}`}
                                className={`${styles.carouselImage} ${window.location.href.includes(image.link) ? styles.active : ''
                                    } ${isHighlighted ? styles.highlighted : ''}`}
                                onClick={() => handleImageClick(image.link)}
                            />
                        )
                    })}
                </div>
            </div>
            <button className={`${styles.arrow} ${styles.right}`} onClick={handleNext}>
                &#8250;
            </button>
        </div> : null
    );
};

export default CarouselNavigation;
